import React from "react";
import {
  Badge,
  Typography,
  Avatar,
  Layout,
  Dropdown,
  MenuProps,
  Popover,
  List,
  Space,
  Button,
  Spin,
  Flex,
  message,
} from "antd";
import {
  LogoutOutlined,
  ToolOutlined,
  UserOutlined,
  CommentOutlined,
  BellFilled,
  ClockCircleOutlined,
  SettingOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import authService from "utils/auth";
import { useQueryClient } from "@tanstack/react-query";
import { useArchiveNotification } from "api/notifications/useArchiveNotification";
import { useMarkReadNotification } from "api/notifications/useMarkReadNotification";
import { formatDate } from "utils/date";
import { storage } from "utils/storage";
import { useMarkAllNotificationRead } from "api/notifications/useMarkAllNotification";

const { Text, Title } = Typography;

const { Header } = Layout;


type HeaderLayoutProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
};

const HeaderLayout = ({ visible, setVisible }: HeaderLayoutProps) => {
  const queryCache = useQueryClient();
  const navigate = useNavigate();

  const user = storage.getUserFromStorage();

  // socket: event for notification

  const [notifications] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [messageCount] = React.useState<number | null>(null);

  const archiveNotificationMutation = useArchiveNotification();
  const markNotificationReadMutation = useMarkReadNotification();
  const markallNotificationMutation = useMarkAllNotificationRead();


  const logout = async () => {
    const logoutMSG: string = await authService.logout(); 
    message.info(logoutMSG); 
    queryCache.clear(); 
    navigate("/"); 
    window.location.reload(); 
  };

  const onClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "logout") {
      logout();
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const handleMarkAllNotification = () => {
    markallNotificationMutation.mutate();
  };

  const ProfileDropdown = () => {
    return (
      <div className="profileDropdown" onClick={() => navigate(`/recruiter/profile/${user?.uid}`)}>
        <Avatar
          size="large"
          className="last"
          style={{
            color: "#f56a00",
            backgroundColor: "#fde3cf",
            boxShadow: "rgba(150, 190, 238, 0.35) 0px 0px 6px 1px",
          }}
        >
          {user?.companyName?.[0]}
        </Avatar>
        <div className="profileDropdownInfo">
          <Text strong>{user?.companyName}</Text>
          <Text type="secondary">{user?.email}</Text>
        </div>
      </div>
    );
  };

  const DropdownMenu = ({ text }: any) => {
    return <Text strong>{text}</Text>;
  };

  const items: any = [
    {
      label: <ProfileDropdown />,
      key: "ProfileDropdown",
    },
    {
      type: "divider",
    },
    {
      icon: <UserOutlined style={{ fontSize: "16px", paddingRight: "6px" }} />,
      key: "settingProfile",
      label: (
        <Link to={`/recruiter/profile/${user?.uid}`}>
          <DropdownMenu text={"Profile"} />
        </Link>
      ),
    },

    {
      icon: <ToolOutlined style={{ fontSize: "16px", paddingRight: "6px" }} />,
      key: "settingApp",
      label: (
        <Link to={"/recruiter/settings"} style={{ fontWeight: 600 }}>
          {"Setting"}
        </Link>
      ),
    },

    {
      type: "divider",
    },

    {
      icon: <LogoutOutlined style={{ fontSize: "16px", paddingRight: "6px" }} />,
      key: "logout",
      label: <Text strong>Logout</Text>,
    },
  ];

  return (
    <>
      <Header style={{ backgroundColor: "white" }}>
        <Flex justify="space-between">
          <MenuOutlined style={{ fontSize: 18 }} onClick={() => setVisible(!visible)} />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              height: "100%",
              gap: "40px",
              paddingRight: "30px",
              marginTop: "8px",
            }}
          >
            <Popover
              trigger={["click"]}
              open={open}
              onOpenChange={handleOpenChange}
              content={
                <div className="__notification__container w-80 max-h-500 min-h-56 md:w-96 overflow-auto">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Title level={5}>Notifications</Title>
                    {notifications?.length > 0 && (
                      <Button
                        type="primary"
                        className="h-8 bg-primary text-white border-none"
                        onClick={handleMarkAllNotification}
                      >
                        Mark all Read
                      </Button>
                    )}
                  </div>

                  {markNotificationReadMutation.isPending ||
                  archiveNotificationMutation.isPending ? (
                    <div
                      style={{
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Spin />
                    </div>
                  ) : (
                    <List
                      itemLayout="horizontal"
                      dataSource={notifications}
                      renderItem={(item: any, index) => (
                        <List.Item
                          style={{
                            padding: "12px",
                            borderRadius: "4px",
                          }}
                        >
                          <List.Item.Meta
                            avatar={
                              item?.isRead ? (
                                <Avatar style={{ backgroundColor: "#87d068" }}>
                                  {item?.body[0]}
                                </Avatar>
                              ) : (
                                <Badge dot={true} offset={[1, 8]}>
                                  <Avatar style={{ backgroundColor: "#87d068" }}>
                                    {item?.body[0]}
                                  </Avatar>
                                </Badge>
                              )
                            }
                            title={<Text strong>{item.subject}</Text>}
                            description={
                              <Space direction="vertical">
                                <Text style={{ fontSize: "13px" }}>{item.body}</Text>
                                <Text type="secondary" style={{ fontSize: "13px" }}>
                                  <ClockCircleOutlined
                                    style={{
                                      marginRight: "5px",
                                      fontSize: "11px",
                                    }}
                                  />
                                  {formatDate(item?.createdAt)}
                                </Text>
                              </Space>
                            }
                          />
                          <NotificationAction
                            id={item?._id}
                            archiveMutation={archiveNotificationMutation}
                            markMutation={markNotificationReadMutation}
                            setOpen={setOpen}
                          />
                        </List.Item>
                      )}
                    />
                  )}
                </div>
              }
            >
              <Badge count={notifications.length}>
                <BellFilled style={{ fontSize: "24px", cursor: "pointer" }} />
              </Badge>
            </Popover>
            <Badge count={messageCount}>
              <CommentOutlined
                style={{ fontSize: "24px", cursor: "pointer" }}
                onClick={() => navigate("/recruiter/message")}
              />
            </Badge>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Dropdown
                menu={{
                  items,
                  onClick,
                }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Avatar
                  src="https://zos.alipayobjects.com/rmsportal/ODTLcjxAfvqbxHnVXCYX.png"
                  size={42}
                  style={{ cursor: "pointer" }}
                />
              </Dropdown>
            </div>
          </div>
        </Flex>
      </Header>
    </>
  );
};

type NotificationProps = {
  id: string;
  archiveMutation: any;
  markMutation: any;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const NotificationAction = ({ id, archiveMutation, markMutation, setOpen }: NotificationProps) => {
  const handleDeleteNotification = () => {
    archiveMutation.mutate(id, {
      onSuccess: () => {
        setOpen(false);
      },
    });
  };

  const markNotificationRead = () => {
    markMutation.mutate(id, {
      onSuccess: () => {
        setOpen(false);
      },
    });
  };

  const items = [
    {
      label: <button onClick={handleDeleteNotification}>Archive</button>,
      key: "0",
    },
    {
      label: <button onClick={markNotificationRead}>Mark as read</button>,
      key: "1",
    },
  ];

  return (
    <Dropdown
      menu={{
        items,
        onClick: () => {},
      }}
      trigger={["click"]}
    >
      <SettingOutlined style={{ fontSize: "16px", cursor: "pointer", marginLeft: "22px" }} />
    </Dropdown>
  );
};

export default HeaderLayout;
